<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="modelValue"
        class="tg-login-overlay"
        :data-theme="light ? 'light' : undefined"
        @click.self="handleOverlayClick"
      >
        <div
          ref="modalContent"
          class="tg-login-modal"
          role="dialog"
          aria-modal="true"
          :aria-label="t('auth.loginWithTelegram')"
          @keydown="handleKeydown"
        >
          <!-- Waiting State -->
          <template v-if="status === 'waiting'">
            <div class="tg-login-modal__icon tg-login-modal__icon--pulse">
              <svg viewBox="0 0 48 48"
width="64"
height="64"
fill="none">
                <circle cx="24"
cy="24"
r="24"
fill="#2AABEE"/>
                <path d="M10.5 23.2l19.7-8.3c1-.4 4.6-1.9 4.6-1.9s1.7-.6 1.5.9c0 .6-.4 2.8-.8 5.2l-2.2 13s-.2 1.4-1.6 1.5c-1.4 0-3.6-2.3-4-2.7-.3-.3-5.8-3.7-7-4.7-.3-.3-.7-.8.1-1.4l7.4-6.8c.8-.8 1.7-2.5-1.8-.4l-10 6.7s-1.3.8-3.7.1c-2.4-.7-5.2-1.7-5.2-1.7s-1.9-1.2 1.3-2.4z" fill="var(--ds-surface)"/>
              </svg>
            </div>
            <h3 class="tg-login-modal__title">{{ t('auth.telegram_login_waiting_title') }}</h3>
            <p class="tg-login-modal__subtitle">{{ t('auth.telegram_login_waiting_description') }}</p>
            <img
              v-if="qrCode"
              :src="qrCode"
              class="tg-login-modal__qr"
              :alt="t('auth.telegram_login_open_bot')"
              width="200"
              height="200"
            />
            <div class="tg-login-modal__countdown">
              {{ t('auth.telegram_login_waiting_countdown', { time: formattedTime }) }}
            </div>
            <a
              :href="botUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="tg-login-modal__open-bot"
            >
              <svg viewBox="0 0 48 48"
width="18"
height="18"
fill="none">
                <circle cx="24"
cy="24"
r="24"
fill="#2AABEE"/>
                <path d="M10.5 23.2l19.7-8.3c1-.4 4.6-1.9 4.6-1.9s1.7-.6 1.5.9c0 .6-.4 2.8-.8 5.2l-2.2 13s-.2 1.4-1.6 1.5c-1.4 0-3.6-2.3-4-2.7-.3-.3-5.8-3.7-7-4.7-.3-.3-.7-.8.1-1.4l7.4-6.8c.8-.8 1.7-2.5-1.8-.4l-10 6.7s-1.3.8-3.7.1c-2.4-.7-5.2-1.7-5.2-1.7s-1.9-1.2 1.3-2.4z" fill="var(--ds-surface)"/>
              </svg>
              {{ t('auth.telegram_login_open_bot') }}
            </a>
            <button type="button" class="tg-login-modal__btn tg-login-modal__btn--secondary" @click="$emit('cancel')">
              {{ t('auth.telegram_login_cancel') }}
            </button>
          </template>

          <!-- Success State -->
          <template v-else-if="status === 'success'">
            <div class="tg-login-modal__icon tg-login-modal__icon--success">
              <svg viewBox="0 0 48 48"
width="64"
height="64"
fill="none">
                <circle cx="24"
cy="24"
r="24"
fill="#4CAF50"/>
                <path d="M14 24l7 7 13-13"
stroke="var(--ds-surface)"
stroke-width="3"
stroke-linecap="round"
stroke-linejoin="round"/>
              </svg>
            </div>
            <h3 class="tg-login-modal__title">{{ t('auth.telegram_login_success') }}</h3>
          </template>

          <!-- Error State -->
          <template v-else-if="status === 'error'">
            <div class="tg-login-modal__icon tg-login-modal__icon--error">
              <svg viewBox="0 0 48 48"
width="64"
height="64"
fill="none">
                <circle cx="24"
cy="24"
r="24"
fill="#F44336"/>
                <path d="M16 16l16 16M32 16l-16 16"
stroke="var(--ds-surface)"
stroke-width="3"
stroke-linecap="round"/>
              </svg>
            </div>
            <h3 class="tg-login-modal__title">
              {{ errorReason === 'timeout' || errorReason === 'expired'
                ? t('auth.telegram_login_timeout')
                : t('auth.telegram_login_error') }}
            </h3>
            <div class="tg-login-modal__actions">
              <button type="button" class="tg-login-modal__btn tg-login-modal__btn--primary" @click="$emit('retry')">
                {{ t('auth.telegram_login_retry') }}
              </button>
              <button type="button" class="tg-login-modal__btn tg-login-modal__btn--secondary" @click="$emit('close')">
                {{ t('auth.telegram_login_close') }}
              </button>
            </div>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, watch, onUnmounted, computed, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import QRCode from 'qrcode'
import { miniAppLinks } from '../utils/telegramDeepLink'

const props = defineProps<{
  modelValue: boolean
  light?: boolean
  status: 'waiting' | 'success' | 'error'
  ttl?: number
  challengeId?: string
  botUsername?: string
  errorReason?: 'expired' | 'timeout' | 'error'
}>()

const emit = defineEmits<{
  cancel: []
  retry: []
  close: []
  'update:modelValue': [value: boolean]
}>()

const { t } = useI18n()
const modalContent = ref<HTMLElement>()
let returnFocus: HTMLElement | null = null
watch(() => props.modelValue, async (open) => {
  if (open) {
    returnFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    await nextTick()
    modalContent.value?.querySelector<HTMLElement>('a[href], button')?.focus({ preventScroll: true })
  } else {
    returnFocus?.focus({ preventScroll: true })
    returnFocus = null
  }
}, { immediate: true })
function handleKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    event.stopPropagation()
    emit('cancel')
  } else if (event.key === 'Tab') {
    const elements = modalContent.value?.querySelectorAll<HTMLElement>('a[href], button:not([disabled])')
    const first = elements?.[0]
    const last = elements?.[elements.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last?.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first?.focus()
    }
  }
}

// ── Countdown ───────────────────────────────────

const remainingSeconds = ref(props.ttl ?? 300)
let countdownTimer: ReturnType<typeof setInterval> | null = null

const formattedTime = computed(() => {
  const m = Math.floor(remainingSeconds.value / 60)
  const s = remainingSeconds.value % 60
  return `${m}:${s.toString().padStart(2, '0')}`
})

const botLinks = computed(() => {
  const bot = (props.botUsername || '').replace(/^@/, '')
  return bot ? miniAppLinks(bot, props.challengeId ? `tglogin_${props.challengeId}` : '') : null
})
const botUrl = computed(() => botLinks.value?.webLink ?? '')
const qrCode = ref('')
const qrUrl = computed(() => props.modelValue && props.status === 'waiting' && props.challengeId ? botUrl.value : '')
watch(qrUrl, async (url) => {
  qrCode.value = ''
  if (!url) return
  try {
    const image = await QRCode.toDataURL(url, { width: 200, margin: 4 })
    if (url === qrUrl.value) qrCode.value = image
  } catch {
    // The explicit bot link remains usable if QR rendering is unavailable.
  }
}, { immediate: true })

const startCountdown = () => {
  stopCountdown()
  remainingSeconds.value = props.ttl ?? 300
  countdownTimer = setInterval(() => {
    if (remainingSeconds.value > 0) {
      remainingSeconds.value--
    } else {
      stopCountdown()
    }
  }, 1000)
}

const stopCountdown = () => {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

// Start/stop countdown based on modal visibility and status
watch(
  () => [props.modelValue, props.status] as const,
  ([visible, status]) => {
    if (visible && status === 'waiting') {
      startCountdown()
    } else {
      stopCountdown()
    }
  },
  { immediate: true },
)

// Reset countdown when ttl changes (retry creates new challenge)
watch(() => props.ttl, (newTtl) => {
  if (newTtl && props.status === 'waiting') {
    remainingSeconds.value = newTtl
  }
})

onUnmounted(() => {
  stopCountdown()
})

// Prevent overlay click from closing modal
const handleOverlayClick = () => {
  // Intentionally do nothing — modal is blocking
}
</script>

<style scoped lang="scss">
.tg-login-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--ds-scrim);
  backdrop-filter: blur(4px);
}

.tg-login-modal {
  background: var(--ds-surface);
  border-radius: var(--p-radius-20);
  padding: 40px 32px;
  max-width: 380px;
  width: calc(100% - 32px);
  box-sizing: border-box;
  max-height: calc(100svh - 32px);
  overflow-y: auto;
  text-align: center;
  box-shadow: 0 20px 60px var(--ds-scrim);
  animation: modal-scale-in 0.3s ease;
}

.tg-login-modal__qr {
  display: block;
  margin: 0 auto 16px;
  max-width: 100%;
  height: auto;
}

@keyframes modal-scale-in {
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
}

.tg-login-modal__icon {
  margin-bottom: 20px;
  display: inline-flex;

  &--pulse {
    animation: icon-pulse 2s ease-in-out infinite;
  }

  &--success {
    animation: icon-pop 0.4s ease;
  }

  &--error {
    animation: icon-shake 0.4s ease;
  }
}

@keyframes icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.08); }
}

@keyframes icon-pop {
  0% { transform: scale(0.5); opacity: 0; }
  70% { transform: scale(1.15); }
  100% { transform: scale(1); opacity: 1; }
}

@keyframes icon-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-6px); }
  40% { transform: translateX(6px); }
  60% { transform: translateX(-4px); }
  80% { transform: translateX(4px); }
}

.tg-login-modal__title {
  font-size: var(--p-text-18);
  font-weight: var(--ds-weight-bold);
  color: var(--ds-text);
  margin: 0 0 8px;
}

.tg-login-modal__subtitle {
  font-size: var(--ds-text-size-body);
  color: #666;
  margin: 0 0 20px;
  line-height: 1.5;
}

.tg-login-modal__countdown {
  font-size: var(--ds-text-size-h-md);
  font-weight: var(--ds-weight-semibold);
  color: #2AABEE;
  margin-bottom: 16px;
  font-variant-numeric: tabular-nums;
}

.tg-login-modal__open-bot {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 20px;
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
  color: #2AABEE;
  text-decoration: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }

  svg {
    flex-shrink: 0;
  }
}

.tg-login-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.tg-login-modal__btn {
  width: 100%;
  padding: 12px 24px;
  border-radius: var(--p-radius-50);
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;

  &--primary {
    background: #2AABEE;
    color: var(--ds-surface);

    &:hover {
      background: #229ED9;
    }

    &:active {
      transform: scale(0.97);
    }
  }

  &--secondary {
    background: transparent;
    color: #999;
    border: 1px solid var(--ds-border);

    &:hover {
      color: #666;
      border-color: #ccc;
    }
  }
}

// Transition
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
